import { EntityViewKanbanColumn, MetadataEntityFieldData } from '@jms/model';
import { SettingsPanelOptionId, SettingsPanelSelectOption } from '@workbuddy/ui/v2/blocks/settings.panel';
import { KanbanValueSetOption } from '../../../model/kanban.value.set';

/**
 * The kanban Columns picker: a grouped multi-select over the column field's value set.
 *
 * Picking a GROUP header combines that group into one column; picking values individually gives each
 * its own. Which grouping exists is metadata (`groupValue`), read off each option's source record, so
 * nothing here knows it is looking at statuses — a field that declares no grouping simply gets a flat
 * list.
 */

/** Prefix marking a synthetic group-header option, kept distinct from any value key. */
const GROUP_OPTION_PREFIX = 'group:';

export function toGroupOptionId(value: string): string {

  return `${GROUP_OPTION_PREFIX}${value}`;
}

export function isGroupOptionId(id: SettingsPanelOptionId): boolean {

  return String(id).startsWith(GROUP_OPTION_PREFIX);
}

export function fromGroupOptionId(id: SettingsPanelOptionId): string {

  return String(id).slice(GROUP_OPTION_PREFIX.length);
}

/**
 * The grouping a field offers, or null for a flat list.
 *
 * Metadata may leave `label` off, so it is resolved here rather than at every call site — the editor
 * needs a name for "Combine by ..." whether or not the schema author wrote one.
 */
export function resolveColumnGroup(field: MetadataEntityFieldData | null | undefined): { path: string; label: string } | null {

  const group = field?.groupValue;
  if (!group?.path) return null;

  return { path: group.path, label: group.label ?? humanizeGroupPath(group.path) };
}

/** 'systemStatus' → 'System status': the leaf segment, de-camelised, read as a sentence not a title. */
function humanizeGroupPath(path: string): string {

  const leaf = path.split('.').pop() ?? path;
  const words = leaf.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

  return words.replace(/^./, match => match.toUpperCase());
}

/**
 * Picker options for a column field's value set.
 *
 * Each group contributes a selectable header option ("On Hold: All") followed by its members, so one
 * list offers both "combine this whole group" and "these specific values". Without a grouping the
 * options are the values alone.
 */
export function toColumnPickerOptions(options: KanbanValueSetOption[] | null | undefined, group: { path: string; label: string } | null, selection?: SettingsPanelOptionId[] | null): SettingsPanelSelectOption[] {

  const values = options ?? [];
  if (!group) return values.map(toValueOption);

  // A ticked header already says "this whole group, as one column", so its members are no longer
  // separate choices — they render greyed rather than as ticks the user could contradict.
  const collapsedGroups = new Set((selection ?? []).filter(isGroupOptionId).map(fromGroupOptionId));

  const byGroup = new Map<string, KanbanValueSetOption[]>();
  const ungrouped: KanbanValueSetOption[] = [];

  for (const option of values) {
    const groupValue = readGroupValue(option, group.path);
    if (!groupValue) {
      ungrouped.push(option);
      continue;
    }

    byGroup.set(groupValue, [...(byGroup.get(groupValue) ?? []), option]);
  }

  const picked: SettingsPanelSelectOption[] = [];

  for (const [groupValue, members] of byGroup) {
    const collapsed = collapsedGroups.has(groupValue);
    picked.push({ id: toGroupOptionId(groupValue), label: `${groupValue}: All`, group: groupValue });
    picked.push(...members.map(member => ({ ...toValueOption(member), group: groupValue, disabled: collapsed })));
  }

  return [...picked, ...ungrouped.map(toValueOption)];
}

/**
 * Picker selection for a view's saved columns.
 *
 * An `expand` rule shows as its members individually — that is exactly what it renders — while a
 * combined rule shows as its group header. Absent columns means the view follows the generator, which
 * the picker presents as everything selected individually, so the list never opens looking empty.
 */
export function toColumnSelection(columns: EntityViewKanbanColumn[] | null | undefined, options: KanbanValueSetOption[] | null | undefined, group: { path: string; label: string } | null): SettingsPanelOptionId[] {

  const values = options ?? [];
  if (!columns?.length) return values.map(option => option.key);

  const selection: SettingsPanelOptionId[] = [];

  for (const column of columns) {
    if (column.rule) {
      const members = values.filter(option => readGroupValue(option, column.rule!.groupBy) === column.rule!.value);
      selection.push(...(column.rule.expand ? members.map(member => member.key) : [toGroupOptionId(column.rule.value)]));
      continue;
    }

    selection.push(...(column.values ?? []));
  }

  return dedupe(selection);
}

/**
 * The columns a picker selection means.
 *
 * A group whose members are ALL individually selected becomes an `expand` rule rather than a frozen
 * list of today's keys — the user's intent was "every value in this group, separately", and a rule is
 * the only spelling of that which keeps working when a tenant adds one tomorrow.
 *
 * Returns undefined when the selection is the pristine default (every value, individually, no
 * grouping applied), so the view stays on the generator instead of freezing the current set into
 * itself.
 */
export function toKanbanColumns(selection: SettingsPanelOptionId[] | null | undefined, options: KanbanValueSetOption[] | null | undefined, group: { path: string; label: string } | null): EntityViewKanbanColumn[] | undefined {

  const values = options ?? [];
  const selected = new Set((selection ?? []).map(id => String(id)));
  if (!selected.size || !values.length) return undefined;

  const selectedGroups = new Set([...selected].filter(isGroupOptionId).map(fromGroupOptionId));
  const selectedValues = new Set([...selected].filter(id => !isGroupOptionId(id)));

  // A fully-selected group is one column per value that follows the grouping, so it is emitted as an
  // expand rule rather than as its members — and a group selected outright collapses to one column.
  //
  // This deliberately also promotes a group whose single member is selected: the two selections are
  // indistinguishable in the picker, and the rule is the safer reading — it keeps the board showing
  // every value in that group as a tenant adds more, where a frozen key list would quietly drop them.
  const fullySelectedGroups = group
    ? groupValuesIn(values, group.path).filter(groupValue => {
        const members = values.filter(option => readGroupValue(option, group.path) === groupValue);
        return members.length > 0 && members.every(member => selectedValues.has(member.key));
      })
    : [];

  if (isPristineDefault(values, selectedValues, selectedGroups)) return undefined;

  const expanded = new Set(fullySelectedGroups);
  const columns: EntityViewKanbanColumn[] = [];
  const emittedGroups = new Set<string>();

  for (const option of values) {
    const groupValue = group ? readGroupValue(option, group.path) : null;

    if (groupValue && selectedGroups.has(groupValue)) {
      if (emittedGroups.has(groupValue)) continue;
      emittedGroups.add(groupValue);
      columns.push({ id: toGroupOptionId(groupValue), rule: { groupBy: group!.path, value: groupValue } });
      continue;
    }

    if (groupValue && expanded.has(groupValue)) {
      if (emittedGroups.has(groupValue)) continue;
      emittedGroups.add(groupValue);
      columns.push({ id: toGroupOptionId(groupValue), rule: { groupBy: group!.path, value: groupValue, expand: true } });
      continue;
    }

    if (selectedValues.has(option.key)) columns.push({ id: option.key, values: [option.key] });
  }

  return columns.length ? columns : undefined;
}

/**
 * Reconciles a picker selection after a GROUP header is ticked or unticked.
 *
 * Ticking a header means "this whole group as one column", so its members stop being separate choices
 * and their ticks are removed. Unticking it has to put something back: the group's members are greyed
 * while the header is ticked, so a bare untick would leave the group with NO selection and no
 * clickable row to fix it — the user would have silently deleted a column and have no way to restore
 * it. Re-ticking every member is both the escape and the honest reading: the group is still rendered,
 * now one column per value, which is exactly an `expand` rule.
 *
 * Returns the selection unchanged when no group's state differs, so a plain value tick costs nothing.
 */
export function reconcileGroupSelection(previous: SettingsPanelOptionId[] | null | undefined, next: SettingsPanelOptionId[] | null | undefined, options: KanbanValueSetOption[] | null | undefined, group: { path: string; label: string } | null): SettingsPanelOptionId[] {

  const selection = (next ?? []).map(id => String(id));
  if (!group) return selection;

  const before = new Set((previous ?? []).filter(isGroupOptionId).map(fromGroupOptionId));
  const after = new Set(selection.filter(isGroupOptionId).map(fromGroupOptionId));
  const values = options ?? [];

  const membersOf = (groupValue: string) => values.filter(option => readGroupValue(option, group.path) === groupValue).map(option => option.key);

  let result = selection;

  for (const groupValue of after) {
    if (before.has(groupValue)) continue;
    const members = new Set(membersOf(groupValue));
    result = result.filter(id => !members.has(String(id)));
  }

  for (const groupValue of before) {
    if (after.has(groupValue)) continue;
    result = [...result, ...membersOf(groupValue)];
  }

  return dedupe(result);
}

/** Every value selected individually, with no group collapsed — what the generator already renders. */
function isPristineDefault(values: KanbanValueSetOption[], selectedValues: Set<string>, selectedGroups: Set<string>): boolean {

  return !selectedGroups.size && values.every(option => selectedValues.has(option.key)) && selectedValues.size === values.length;
}

function groupValuesIn(values: KanbanValueSetOption[], path: string): string[] {

  const seen: string[] = [];

  for (const option of values) {
    const groupValue = readGroupValue(option, path);
    if (groupValue && !seen.includes(groupValue)) seen.push(groupValue);
  }

  return seen;
}

function toValueOption(option: KanbanValueSetOption): SettingsPanelSelectOption {

  return { id: option.key, label: option.name, color: option.color ?? undefined } as SettingsPanelSelectOption;
}

function readGroupValue(option: KanbanValueSetOption, path: string): string | null {

  const source = option?.data ?? option;
  const value = path.split('.').reduce<any>((acc, part) => acc?.[part], source);
  return value == null ? null : String(value);
}

function dedupe(ids: SettingsPanelOptionId[]): SettingsPanelOptionId[] {

  return [...new Set(ids.map(id => String(id)))];
}
